import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  Search,
  X,
  Download,
  Trash2,
  FileText,
  IndianRupee,
  Files,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { normalizeWhatsApp } from "@/lib/whatsapp";
import { BRAND } from "@/brand";
import QuoteBuilder from "@/components/QuoteBuilder";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Top-level Billing page — lists every quotation across every client.
 * Includes search, KPI header, PDF download, WhatsApp share, delete, and a
 * "New Quotation" action that lets the user first pick a client (submission).
 */
export default function BillingPanel() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [builderFor, setBuilderFor] = useState(null); // a submission object

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotations");
      setQuotes(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const q = search.trim().toLowerCase();
  const visible = useMemo(() => {
    if (!q) return quotes;
    return quotes.filter((qt) => {
      const c = qt.client_snapshot || {};
      const hay = [qt.quote_no, c.name, c.mobile, c.company, c.location, qt.created_by?.name, qt.created_by?.email]
        .map((x) => (x || "").toString().toLowerCase()).join(" | ");
      return hay.includes(q);
    });
  }, [quotes, q]);

  const totals = useMemo(() => {
    let count = quotes.length, sum = 0;
    for (const qt of quotes) sum += Number(qt.grand_total || 0);
    return { count, sum };
  }, [quotes]);

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${q.quote_no.replace(/\//g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };

  const openWhatsApp = (q) => {
    const c = q.client_snapshot || {};
    const num = normalizeWhatsApp(c.mobile);
    if (!num) { toast.error("Client mobile not valid for WhatsApp"); return; }
    const msg = `Hello ${c.name || "there"},\n\nPlease find your quotation *${q.quote_no}* from *${BRAND.companyName}*.\n\nGrand Total: ₹${Number(q.grand_total).toLocaleString("en-IN")}\n\nWe will share the PDF right after this message.\n\nThank you!`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`Delete ${q.quote_no}?`)) return;
    try {
      await api.delete(`/quotations/${q.id}`);
      setQuotes((prev) => prev.filter((x) => x.id !== q.id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatError(e)); }
  };

  const openBuilderFor = (submission) => {
    setPickerOpen(false);
    setBuilderFor(submission);
  };
  const closeBuilder = () => {
    setBuilderFor(null);
    refresh(); // reload the list so any new quote appears
  };

  return (
    <div className="space-y-4" data-testid="billing-panel">
      {/* KPIs + Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Total Quotations" value={totals.count} icon={Files} tint="blue" />
        <StatCard label="Total Quoted Value" value={rupee(totals.sum)} icon={IndianRupee} tint="emerald" isText />
        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-blue-700 rounded-2xl p-5 flex items-center justify-between text-white shadow-lg">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-sky-200 font-semibold">Quick Action</div>
            <div className="mt-1 text-lg font-bold">Create a new quotation</div>
            <div className="text-xs text-slate-300 mt-0.5">Pick a client from your leads, add products.</div>
          </div>
          <button
            onClick={() => setPickerOpen(true)}
            data-testid="billing-new-quote"
            className="rounded-xl bg-white text-blue-800 hover:bg-slate-100 px-4 py-2.5 text-sm font-bold inline-flex items-center gap-1.5 shadow-md whitespace-nowrap"
          >
            <Plus className="w-4 h-4" /> New Quote
          </button>
        </div>
      </div>

      {/* Search + list */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-lg">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by quote no, client, mobile, company, worker…"
              data-testid="billing-search"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-9 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white"
            />
            {search && (
              <button onClick={() => setSearch("")} aria-label="Clear" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : visible.length === 0 ? (
          <div className="p-14 text-center">
            <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
            <div className="text-sm text-slate-500">{q ? "No quotations match your search." : "No quotations yet — hit New Quote to build your first one."}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-widest text-slate-600">
                  <th className="px-5 py-3">Quote No.</th>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Client</th>
                  <th className="px-5 py-3">Location</th>
                  <th className="px-5 py-3 text-center">Items</th>
                  <th className="px-5 py-3 text-right">Grand Total</th>
                  <th className="px-5 py-3">By</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((q) => {
                  const c = q.client_snapshot || {};
                  return (
                    <tr key={q.id} data-testid={`billing-row-${q.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-5 py-3 font-mono text-xs font-bold text-blue-700">{q.quote_no}</td>
                      <td className="px-5 py-3 text-slate-600 text-xs">{new Date(q.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                      <td className="px-5 py-3">
                        <div className="font-semibold text-slate-900">{c.name || "—"}</div>
                        <div className="text-xs text-slate-500">{c.mobile || "—"}</div>
                      </td>
                      <td className="px-5 py-3 text-slate-600 text-xs">{c.location || "—"}</td>
                      <td className="px-5 py-3 text-center text-slate-700">{q.items?.length || 0}</td>
                      <td className="px-5 py-3 text-right font-bold text-slate-900">{rupee(q.grand_total)}</td>
                      <td className="px-5 py-3 text-xs text-slate-600 truncate max-w-[140px]">{q.created_by?.name || q.created_by?.email || "—"}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <button onClick={() => downloadPdf(q)} data-testid={`billing-download-${q.id}`} title="Download PDF" className="text-blue-700 hover:text-blue-900 mr-3">
                          <Download className="w-4 h-4 inline" />
                        </button>
                        <button onClick={() => openWhatsApp(q)} data-testid={`billing-wa-${q.id}`} title="Send on WhatsApp" className="text-emerald-600 hover:text-emerald-700 mr-3">
                          <WhatsAppIcon className="w-4 h-4 inline" />
                        </button>
                        <button onClick={() => deleteQuote(q)} data-testid={`billing-delete-${q.id}`} title="Delete" className="text-rose-600 hover:text-rose-700">
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pickerOpen && <ClientPicker onPick={openBuilderFor} onClose={() => setPickerOpen(false)} />}
      {builderFor && <QuoteBuilder submission={builderFor} onClose={closeBuilder} />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tint, isText }) {
  const map = {
    blue: "bg-blue-100 text-blue-700",
    emerald: "bg-emerald-100 text-emerald-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <span className={`w-10 h-10 rounded-xl inline-flex items-center justify-center ${map[tint]}`}>
          <Icon className="w-5 h-5" />
        </span>
        <div>
          <div className="text-[11px] font-semibold text-slate-500">{label}</div>
          <div className={`mt-0.5 ${isText ? "text-xl" : "text-2xl"} font-black text-slate-900 tracking-tight`}>{value}</div>
        </div>
      </div>
    </div>
  );
}

function ClientPicker({ onPick, onClose }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/submissions");
        // De-duplicate by client_mobile (or id) so the picker isn't spammed with every follow-up.
        const seen = new Set();
        const uniq = [];
        for (const s of data) {
          const key = s.client_mobile || s.id;
          if (seen.has(key)) continue;
          seen.add(key);
          uniq.push(s);
        }
        setSubs(uniq);
      } catch (e) { toast.error(formatError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const qq = query.trim().toLowerCase();
  const visible = qq
    ? subs.filter((s) => [s.client_name, s.client_mobile, s.client_company, s.location].map((x) => (x || "").toString().toLowerCase()).join(" | ").includes(qq))
    : subs;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden border border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700">Step 1</div>
            <h3 className="font-display font-bold text-slate-900 text-lg mt-0.5">Pick a client</h3>
          </div>
          <button onClick={onClose} data-testid="picker-close" className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, mobile, company, location…"
              data-testid="picker-search"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
          ) : visible.length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-500">No clients match.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {visible.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => onPick(s)}
                    data-testid={`picker-pick-${s.id}`}
                    className="w-full text-left px-5 py-3 hover:bg-blue-50 flex items-center justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{s.client_name}</div>
                      <div className="text-xs text-slate-500 truncate">{s.client_mobile} · {s.client_company || "—"} · {s.location || "—"}</div>
                    </div>
                    <span className="text-xs font-semibold text-blue-700 shrink-0">Choose →</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
